import { useState, useEffect, useCallback } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { AuthModal } from '@/components/auth/AuthModal';
import { useAuth } from '@/contexts/AuthContext';
import {
  PiggyBank,
  FileText,
  BarChart3,
  Sparkles,
  Building2,
  Users,
} from 'lucide-react';

// Session storage keys
const PLAN_STORAGE_KEY = 'sapling-selected-plan';
const OPEN_SIGNUP_KEY = 'sapling-open-signup';

const features = [
  {
    icon: PiggyBank,
    title: 'Personal & Business',
    description: 'Track both in one app with clear separation',
    color: 'sapling-primary-100',
  },
  {
    icon: FileText,
    title: 'Smart PDF Import',
    description: 'Upload bank statements, auto-parse transactions',
    color: 'sapling-secondary-100',
  },
  {
    icon: BarChart3,
    title: 'CPA-Ready Reports',
    description: 'Balance Sheet, P&L, Tax summaries ready to send',
    color: 'sapling-tertiary-100',
  },
  {
    icon: Sparkles,
    title: 'Auto-Categorization',
    description: 'AI-powered rules learn your spending patterns',
    color: 'sapling-logo-orange',
  },
  {
    icon: Building2,
    title: 'Multi-Business',
    description: 'Manage unlimited businesses from one account',
    color: 'sapling-logo-blue',
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Invite staff with role-based permissions',
    color: 'sapling-logo-green',
  },
];

export function LandingPage() {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');
  const navigate = useNavigate();
  const { user, initialized } = useAuth();

  // Get plan from URL query params
  const search = useSearch({ strict: false }) as { plan?: string };
  const planParam = search?.plan;

  // Redirect authenticated users to /app (AuthGate will handle routing from there)
  useEffect(() => {
    if (initialized && user) {
      // Close modal if open and redirect to app
      setAuthModalOpen(false);
      navigate({ to: '/app' });
    }
  }, [initialized, user, navigate]);

  // Store plan param in sessionStorage when it changes
  useEffect(() => {
    if (planParam && ['solo', 'pro', 'team'].includes(planParam)) {
      sessionStorage.setItem(PLAN_STORAGE_KEY, planParam);
    }
  }, [planParam]);

  // Auto-open signup modal when redirected from /signup or when ?plan= param is present
  useEffect(() => {
    // Don't auto-open if user is already authenticated
    if (user) return;

    const shouldOpenSignup = sessionStorage.getItem(OPEN_SIGNUP_KEY);

    if (shouldOpenSignup === 'true') {
      // Clear the flag so it doesn't re-open on refresh
      sessionStorage.removeItem(OPEN_SIGNUP_KEY);
      setAuthMode('signup');
      setAuthModalOpen(true);
    } else if (planParam && ['solo', 'pro', 'team'].includes(planParam)) {
      // Also auto-open if plan param is directly in URL
      setAuthMode('signup');
      setAuthModalOpen(true);
    }
  }, [planParam, user]);

  const handleLoginClick = () => {
    setAuthMode('login');
    setAuthModalOpen(true);
  };

  const handleSignupClick = () => {
    setAuthMode('signup');
    setAuthModalOpen(true);
  };

  // Handle successful authentication
  // Note: The useEffect above will automatically redirect to /app when user state updates
  // AuthGate will then handle routing based on subscription status and stored plan
  const handleAuthSuccess = useCallback(() => {
    // Close the modal - the auth state change will trigger redirect via useEffect
    setAuthModalOpen(false);
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <Header
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
      />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-sapling-primary-100/5 to-background py-20 sm:py-32">
          <div className="container relative">
            <div className="mx-auto max-w-3xl text-center">
              <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                Simple Accounting
                <span className="block text-sapling-primary-100">
                  That Grows With You
                </span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground sm:text-xl">
                Track personal and business finances with AI-powered automation.
                Get tax-ready reports without the complexity.
              </p>
              <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                <Button size="xl" onClick={handleSignupClick}>
                  Get Started
                </Button>
                <Button size="xl" variant="outline" onClick={handleLoginClick}>
                  Log In
                </Button>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                Starting at just $10/month. Cancel anytime.
              </p>
            </div>
          </div>

          {/* Background decoration */}
          <div className="absolute inset-0 -z-10 overflow-hidden">
            <div className="absolute -top-1/2 left-1/2 h-[800px] w-[800px] -translate-x-1/2 rounded-full bg-sapling-primary-100/5 blur-3xl" />
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Everything You Need
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Powerful features designed for small business owners with
                limited accounting knowledge.
              </p>
            </div>

            <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="group rounded-card border bg-card p-6 transition-shadow hover:shadow-lg"
                >
                  <div
                    className={`inline-flex h-12 w-12 items-center justify-center rounded-btn bg-${feature.color}/10`}
                  >
                    <feature.icon
                      className={`h-6 w-6 text-${feature.color}`}
                    />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold">
                    {feature.title}
                  </h3>
                  <p className="mt-2 text-muted-foreground">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-sapling-primary-200 to-sapling-primary-100 py-16">
          <div className="container">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Ready to Get Started?
              </h2>
              <p className="mt-4 text-lg text-white/90">
                Join thousands of small business owners who trust Sapling for
                their accounting needs.
              </p>
              <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                <Button
                  size="xl"
                  onClick={handleSignupClick}
                  className="bg-white text-sapling-primary-200 hover:bg-white/90"
                >
                  Start Your Subscription
                </Button>
                <a
                  href="https://oasisautokonnect.com/sapling#pricing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-white/90 underline underline-offset-4 font-medium"
                >
                  Compare Plans
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
        defaultView={authMode}
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
}
